<script setup lang="ts">
import { twMerge } from "tailwind-merge";
import { computed, useId } from "vue";

/**
 * Uma caixa de marcar ou um botão de rádio, com o rótulo ao lado.
 *
 * Não usa o `Field`: aqui o rótulo fica À DIREITA do controle e o `<label>` o
 * envolve, que é o que faz a palavra inteira ser área de clique. Dica e erro
 * continuam embaixo, com a mesma regra de sempre — só ocupam altura quando
 * existem.
 *
 * Rádio e caixa são o mesmo componente porque são o mesmo desenho: muda o
 * `type`, e num grupo de rádio muda o `name`, que cai pelos atributos.
 */
defineOptions({ inheritAttrs: false });

const props = defineProps<{
	/**
	 * O texto ao lado da caixa — e a área de clique dele junto.
	 *
	 * Opcional porque dentro de um envelope o rótulo já está desenhado acima, e
	 * escrevê-lo de novo aqui o faz aparecer duas vezes. Sem ele, quem chama
	 * precisa dar um `aria-label`.
	 */
	label?: string;
	/** Marcado. Num grupo de rádio, "esta é a opção escolhida". */
	modelValue?: boolean;
	type?: "checkbox" | "radio";
	hint?: string;
	error?: string;
	/** Inválido sem mensagem própria — o erro está sendo desenhado por fora. */
	invalid?: boolean;
	describedBy?: string;
	id?: string;
	disabled?: boolean;
	class?: string;
}>();
const emit = defineEmits<{ "update:modelValue": [boolean] }>();

const generated = useId();
const controlId = computed(() => props.id ?? generated);
const hintId = computed(() => `${controlId.value}-hint`);
const errorId = computed(() => `${controlId.value}-error`);
const wrong = computed(() => props.invalid ?? Boolean(props.error));

const describes = computed(() => {
	const ids = [
		props.describedBy,
		props.hint ? hintId.value : null,
		props.error ? errorId.value : null,
	].filter((id): id is string => Boolean(id));
	return ids.length > 0 ? ids.join(" ") : undefined;
});

const row = computed(() =>
	twMerge(
		"flex items-center gap-2.5 text-sm text-fg",
		props.disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
		props.class,
	),
);
</script>

<template>
	<div class="space-y-1">
		<label :for="controlId" :class="row">
			<input
				:id="controlId"
				:type="type ?? 'checkbox'"
				:checked="modelValue === true"
				:disabled="disabled"
				:aria-invalid="wrong"
				:aria-describedby="describes"
				class="size-4 shrink-0 accent-accent"
				v-bind="$attrs"
				@change="emit('update:modelValue', ($event.target as HTMLInputElement).checked)"
			/>
			<span v-if="label">{{ label }}</span>
		</label>

		<p v-if="hint" :id="hintId" class="text-xs text-fg-subtle">{{ hint }}</p>
		<p v-if="error" :id="errorId" class="text-xs text-danger">{{ error }}</p>
	</div>
</template>
